import React, { useContext, useEffect, useState, useRef, useCallback } from "react";
import "./style.scss";
import axios from "axios";
import { CloseOutlined, DownloadOutlined } from "@ant-design/icons";
import { Button, Image, Input, InputNumber, message, Modal, Radio } from "antd";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  useCompleteDraftSkuOnSegregation,
  useGetSkuDetail,
  useLogoutUser,
  useUpdateProcessFilesOnSku,
  useCanNotProcessOnSeggregation,
  useGetCSVForBatchFiles,
} from "../../../hooks/useSeggregation";
import { Link, useNavigate, useParams } from "react-router-dom";
import { SeggreagtionContext } from "../../../context/seggregationProvider";
import { useUpload } from "../../../context/seggUploadFileContext";
import ReactJson from "@microlink/react-json-view";
// import seggregationApi from "../../apis";

const DraggableSkuImage = ({
  skuFile,
  isSelected,
  previewDimensions,
  onImageLoad,
}) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: skuFile._id,
    data: { sku: skuFile },
  });

  return (
    <div
      ref={setNodeRef}
      className={`select_image_wrapper ${isSelected ? "selectedFile" : ""} ${isDragging ? "is-dragging" : ""}`}
      {...listeners}
      {...attributes}
    >
      <Image
        src={skuFile?.image?.thumbPath?.url}
        alt={skuFile?.image?.name}
        preview={{
          src: skuFile?.image?.imagePath.url,
          title: previewDimensions
            ? `Dimensions: ${previewDimensions.width} x ${previewDimensions.height}`
            : "Preview",
        }}
        onClick={(e) => onImageLoad(e, skuFile?.image?.imagePath.url)}
      />
    </div>
  );
};

const GridDropCell = ({
  rowIndex,
  colIndex,
  cell,
  isDisabled,
  onDelete,
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `cell-${rowIndex}-${colIndex}`,
    disabled: isDisabled || !!cell,
    data: { rowIndex, colIndex },
  });

  return (
    <div
      ref={setNodeRef}
      className={`grid_cell ${cell ? "occupied" : isDisabled ? "disabled" : ""} ${isOver && !isDisabled && !cell ? "is-over" : ""}`}
    >
      {cell && (
        <>
          <img src={cell.image?.thumbPath?.url} alt={cell.image?.name} />
          <CloseOutlined
            style={{ fontSize: "14px", color: "red" }}
            className="delete_icon"
            onClick={() => onDelete(rowIndex, colIndex)}
          />
        </>
      )}
    </div>
  );
};

const BSTSeggregation = () => {
  const [selectedImages, setSelectedImages] = useState([]);
  const [previewDimensions, setPreviewDimensions] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showCannotProcessModal, setShowCannotProcessModal] = useState(false);
  const [showSubmitContentModal, setShowSubmitContentModal] = useState(false);
  const [showBatchSheetModal, setShowBatchSheetModal] = useState(false);
  const [skuFiles, setSkuFiles] = useState([]);
  const params = useParams();
  const navigate = useNavigate();
  // Removed isCCRequired and isComboSku state - now handled by button clicks
  const { segg_queryParams, segg_logoutPayload } =
    useContext(SeggreagtionContext);
  const [selectedMarketplace, setSelectedMarketplace] = useState();
  const [richTextHtml, setRichTextHtml] = useState();
  const [skuDetails, setSkuDetails] = useState();
  const [articleShade, setArticleShade] = useState("dark");
  const [packSize, setPackSize] = useState(null);
  const [uploadFiles, setUploadFiles] = useState({});
  const {
    startUpload,
    pauseUpload,
    uploadedFolders,
    setUploadedFolders,
    startUploading,
    setStartUploading,
  } = useUpload();

  // Check if any folder is currently uploading
  const isUploading = startUploading || uploadedFolders.some(
    (folder) => folder.memory.uploading
  );
  const [grid, setGrid] = useState(
    Array(9)
      .fill(null)
      .map(() => Array(4).fill(null))
  );
  const [disabledColumns, setDisabledColumns] = useState(
    Array(9)
      .fill(null)
      .map(() => Array(4).fill(false))
  );
  const [activeDragId, setActiveDragId] = useState(null);
  const fileInputRef = useRef(null);
  const prevUploadingRef = useRef(false);
  const completedFolderIdsRef = useRef(new Set());
  const onSuccessGetSkuDetails = (res) => {
    if (res.status === 200) {
      setSkuFiles(res.data.skuFiles);
      setSkuDetails(res.data);
      setSelectedMarketplace(
        res?.data?.draftSku?.toProcessFilesDraft[0]?.marketplace?.name
      );
      setRichTextHtml(res?.data?.batch?.richTextDescriptionHtml?.url);
    }
  };
  const onErrorGetSkuDetails = (err) => {
    console.log(err);
  };
  const { refetch: getSkuDetails } = useGetSkuDetail(
    "getSKudetails",
    params?.skuId,
    onSuccessGetSkuDetails,
    onErrorGetSkuDetails
  );

  // Watch for upload completion and refresh SKU details
  useEffect(() => {
    // Check for newly completed folders
    uploadedFolders.forEach((folder) => {
      const isCompleted =
        !folder.memory.uploading &&
        folder.memory.totalUploaded > 0 &&
        folder.memory.totalUploaded === folder.memory.totalFiles &&
        folder.folderId !== null;

      if (isCompleted && !completedFolderIdsRef.current.has(folder.folderId)) {
        // New folder completed - mark it as processed
        completedFolderIdsRef.current.add(folder.folderId);

        // Refresh SKU details when upload completes
        getSkuDetails();
      }
    });

    // Track when upload starts
    if (startUploading) {
      prevUploadingRef.current = true;
    }
  }, [uploadedFolders, startUploading, getSkuDetails]);

  //   const handleSkuStart = async () => {
  //     const result = await seggregationApi.skuSeggStart(skuDetails?._id);
  //     return result;
  //   };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  const handleDndDragStart = ({ active }) => {
    setActiveDragId(active.id);
  };

  const handleDndDragEnd = ({ active, over }) => {
    setActiveDragId(null);
    if (!over?.data?.current) return;

    const { rowIndex, colIndex } = over.data.current;
    const imageId = active.id;
    const draggedImage =
      skuFiles.find((sku) => sku?._id === imageId) ||
      selectedImages.find((img) => img.id === imageId);

    if (draggedImage && !grid[rowIndex][colIndex]) {
      const updatedGrid = grid.map((row, rIndex) =>
        row.map((col, cIndex) => {
          if (rIndex === rowIndex && cIndex === colIndex) {
            return draggedImage;
          }
          return col;
        })
      );

      const updatedDisabledColumns = disabledColumns.map((row, rIndex) =>
        row.map((disabled, cIndex) => {
          if (rIndex === rowIndex && cIndex !== colIndex) {
            return true;
          }
          return disabled;
        })
      );
      const isAlreadySelected = selectedImages.some(
        (img) => img?._id === draggedImage?._id
      );

      if (!isAlreadySelected) {
        setSelectedImages([...selectedImages, draggedImage]);
      }

      setGrid(updatedGrid);
      setDisabledColumns(updatedDisabledColumns);
    }
  };

  const handleDndDragCancel = () => {
    setActiveDragId(null);
  };

  const activeDraggedSku = activeDragId
    ? skuFiles.find((sku) => sku?._id === activeDragId)
    : null;

  const handleDeleteImage = (rowIndex, colIndex) => {
    const removedImage = grid[rowIndex][colIndex];
    const imageCountInGrid = grid.reduce(
      (count, row) =>
        count +
        row.filter((cell) => cell && cell?._id === removedImage?._id).length,
      0
    );

    const updatedGrid = grid.map((row, rIndex) =>
      row.map((cell, cIndex) => {
        if (rIndex === rowIndex && cIndex === colIndex) {
          return null;
        }
        return cell;
      })
    );

    const updatedDisabledColumns = disabledColumns.map((row, rIndex) =>
      row.map((disabled, cIndex) => {
        if (rIndex === rowIndex) {
          return false;
        }
        return disabled;
      })
    );
    if (imageCountInGrid === 1) {
      // setSkuFiles([...skuFiles, removedImage]);
      setSelectedImages(
        selectedImages.filter((img) => img?._id !== removedImage?._id)
      );
    }

    setGrid(updatedGrid);
    setDisabledColumns(updatedDisabledColumns);
  };
  const onSuccessCompleteDraftSkuOnSegregation = (res) => {
    if (res.status === 200) {
      message.success("Sku Completed");
      navigate(`/seggregation-batch-loading/${segg_queryParams}`);
    }
  };

  const onErrorCompleteDraftSkuOnSegregation = (err) => {
    console.log(err);
  };
  const { mutate: completeDraftSkuOnSegregation } =
    useCompleteDraftSkuOnSegregation(
      onSuccessCompleteDraftSkuOnSegregation,
      onErrorCompleteDraftSkuOnSegregation
    );
  const handleComplete = () => {
    completeDraftSkuOnSegregation(params?.skuId);
  };
  const onSuccess = (res) => {
    if (res?.status === 200) {
      handleComplete();
    }
  };
  const onError = (err) => { };
  const { mutate: updateProcessFiles } = useUpdateProcessFilesOnSku(
    onSuccess,
    onError
  );

  const onSuccessCanNotProcessOnSeggregation = (res) => {
    if (res.status === 200) {
      message.success("Sku Cannot Process");
      logoutUser(segg_logoutPayload);
      navigate(`/seggregation-batch-loading/${segg_queryParams}`);
    }
  };
  const onErrorCanNotProcessOnSeggregation = (err) => {
    console.log(err);
  };
  const { mutate: canNotProcessOnSeggregation } =
    useCanNotProcessOnSeggregation(
      onSuccessCanNotProcessOnSeggregation,
      onErrorCanNotProcessOnSeggregation
    );
  const submitSeggSeq = async (ccRequired, comboSku) => {
    let payloadData = [];
    console.log(grid);
    const rowsWithData = grid.filter((row) =>
      row.some((data) => data !== null)
    );
    if (rowsWithData.length < 2) {
      message.error("Please fill at least 2 rows");
      return;
    }
    grid.map((gridData, index) => {
      gridData.map((data, i) => {
        if (data) {
          let imageType;
          if (i === 0) {
            imageType = "Model";
          } else if (i === 1) {
            imageType = "Ghost";
          } else if (i === 2) {
            imageType = "Product";
          } else if (i === 3) {
            imageType = "Detail";
          }
          console.log(data);
          payloadData.push({
            mainFileId: data?._id,
            seqNo: index + 1,
            shootType: imageType,
          });
        }
      });
    });
    console.log(payloadData);
    const payload = {
      draftSkuId: params?.skuId,
      marketplaceId: params?.marketplaceId,
      toProcessFiles: payloadData,
      autoGenerateGuideline: true,
      isCCRequired: ccRequired,
      isComboSku: comboSku,
      articleShade,
      packSize,
    };
    console.log("payload", payload);
    updateProcessFiles(payload);
  };

  const rejectSku = async () => {
    if (!rejectReason.trim()) {
      message.error("Enter Reason");
      return;
    }
    const payload = {
      reason: rejectReason,
    };
    // const markSkuRejected = await seggregationApi.rejectSku(
    //   payload,
    //   skuDetails?._id
    // );
    // if (markSkuRejected.status == 200) {
    //   message.success("Sku Rejected");
    //   setTimeout(() => {
    //     reRender();
    //   }, 1000);
    // }
  };
  const handleSubmit = (ccRequired, comboSku) => {
    submitSeggSeq(ccRequired, comboSku);
  };
  const handleSubmitContentOnly = () => {
    const payload = {
      draftSkuId: params?.skuId,
      marketplaceId: params?.marketplaceId,
      toProcessFiles: [],
      autoGenerateGuideline: true,
      isContentOnly: true,
      articleShade,
      packSize,
    };
    updateProcessFiles(payload);
  };
  const handleConfirmSubmitContentOnly = () => {
    handleSubmitContentOnly();
    setShowSubmitContentModal(false);
  };
  const handleLoad = (e, fullSrc) => {
    e.preventDefault();
    const img = new window.Image();
    img.src = fullSrc;
    img.onload = () => {
      setPreviewDimensions({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    };
  };
  const onSuccessLogout = () => { };
  const { mutate: logoutUser } = useLogoutUser(onSuccessLogout);

  const handleResetVersion = () => {
    logoutUser(segg_logoutPayload);
    localStorage.removeItem("seggregationVersion");
    navigate(`/seggregation-batch-loading/${segg_queryParams}`);
  };
  const handleSkipVersion = () => {
    logoutUser(segg_logoutPayload);
    navigate(`/seggregation-batch-loading/${segg_queryParams}`);
  };
  const handleCannotProcess = () => {
    setShowCannotProcessModal(true);
  };
  const handleConfirmCannotProcess = () => {
    canNotProcessOnSeggregation(params?.skuId);
    setShowCannotProcessModal(false);
  };
  const handleUploadFileClick = () => {
    fileInputRef.current?.click();
  };
  // const handleFileChange = (e) => {
  //   const files = e.target.files;
  //   if (files && files.length > 0) {
  //     console.log("Selected files:", files);
  //     // Handle file upload logic here
  //   }
  // };
  const handleFileChange = useCallback(
    (files) => {
      if (!files || files.length === 0) {
        return;
      }
      const batchId = skuDetails?.batch?._id;
      if (!batchId) {
        message.error("Batch ID not found. Please wait for SKU details to load.");
        return;
      }
      setUploadFiles((prev) => ({
        ...prev,
        categoryImage: files[0],
      }));
      startUpload(Array.from(files), batchId, params?.skuId);
    },
    [skuDetails, startUpload, params?.skuId]
  );
  const onSuccessDownloadCSV = (res) => {
    if (res?.status === 200) {
      try {
        // Handle CSV download
        let csvContent = res.data;
        // Ensure csvContent is a string
        if (typeof csvContent !== 'string') {
          csvContent = typeof csvContent === 'object' ? JSON.stringify(csvContent) : String(csvContent);
        }

        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);

        let filename = "batch_files";
        if (res.headers?.["content-disposition"]) {
          const filenameMatch = res.headers["content-disposition"]
            .split("filename=")[1]
            ?.replace(/"/g, "")
            ?.split(".")[0];
          if (filenameMatch) {
            filename = filenameMatch;
          }
        } else {
          filename = `batch_${skuDetails?.batch?._id || "files"}`;
        }

        link.setAttribute("download", `${filename}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);
        message.success("CSV file downloaded successfully");
      } catch (error) {
        console.error("Error downloading CSV:", error);
        message.error("Failed to process CSV file");
      }
    }
  };
  const onErrorDownloadCSV = (err) => {
    console.log(err);
    message.error("Failed to download CSV file");
  };
  const { mutate: downloadCSVForBatch } = useGetCSVForBatchFiles(
    onSuccessDownloadCSV,
    onErrorDownloadCSV
  );
  const handleDownloadCSV = () => {
    const batchId = skuDetails?.batch?._id;
    if (!batchId) {
      message.error("Batch ID not found. Please wait for SKU details to load.");
      return;
    }
    downloadCSVForBatch(batchId);
  };
  const getRejetSkuModal = () => {
    return (
      <Modal
        open={showRejectModal}
        title="Enter Reason"
        onOk={() => rejectSku()}
        onCancel={() => setShowRejectModal(false)}
        okText="Submit"
      >
        <Input
          placeholder="Enter reason"
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
        />
      </Modal>
    );
  };
  const getCannotProcessModal = () => {
    return (
      <Modal
        open={showCannotProcessModal}
        title="Confirm Cannot Process"
        onOk={handleConfirmCannotProcess}
        onCancel={() => setShowCannotProcessModal(false)}
        okText="Confirm"
        cancelText="Cancel"
        centered
      >
        <p>Are you sure you want to mark this SKU as "Cannot Process"?</p>
        <p style={{ color: "red", marginTop: "10px", marginBottom: 0 }}>
          This sku will be removed from the batch
        </p>
      </Modal>
    );
  };
  const getBatchSheetModal = () => {
    const batchSheetRows = skuDetails?.draftSku?.batchSheetRows;
    return (
      <Modal
        open={showBatchSheetModal}
        title="Batch Sheet Rows"
        onCancel={() => setShowBatchSheetModal(false)}
        footer={null}
        width={800}
        centered
      >
        {Array.isArray(batchSheetRows) && batchSheetRows.length > 0 ? (
          <div style={{ maxHeight: "70vh", overflow: "auto" }}>
            <ReactJson
              src={batchSheetRows}
              name={false}
              collapsed={false}
              displayDataTypes={false}
              enableClipboard={true}
              style={{ fontSize: "13px" }}
            />
          </div>
        ) : (
          <p>No Data auto Detected, Please Read Excel manually and Assign</p>
        )}
      </Modal>
    );
  };
  const getSubmitContentModal = () => {
    return (
      <Modal
        open={showSubmitContentModal}
        title="Submit Only Content"
        onOk={handleConfirmSubmitContentOnly}
        onCancel={() => setShowSubmitContentModal(false)}
        okText="Confirm"
        cancelText="Cancel"
        centered
      >
        <p>Are you sure you want to submit this SKU with content only?</p>
        <p style={{ color: "#faad14", marginTop: "10px", marginBottom: 0 }}>
          This action will proceed without assigning any images.
        </p>
      </Modal>
    );
  };
  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDndDragStart}
      onDragEnd={handleDndDragEnd}
      onDragCancel={handleDndDragCancel}
    >
    <div className="seggregation_main_wrapper">
      <div className="left_images_wrapper">
        {/* <div className="name_wrapper">
          <span className="heading">
            Job Name: <span className="value">{skuDetails?.job?.name}</span>
          </span>
          <span className="heading">
            SKU Name: <span className="value">{skuDetails?.name}</span>
          </span>
        </div> */}
        <div className="images_container">
          <div className="select_image_heading">
            <div className="left_heading">Job Name: <span>{skuDetails?.batch?.name} </span>
              <span className="batchNum">({skuDetails?.batch?.batchNumber}) </span>
            </div>
            <div className="heading">Display Name:
              <span className="batchNum">{skuDetails?.draftSku?.displayName} </span>
            </div>
            <div className="right_heading">
              Marketplace : {selectedMarketplace}
            </div>
          </div>
          <div className="select_image_section">
            {skuFiles.length > 0 &&
              skuFiles.map((skuFile) => (
                <DraggableSkuImage
                  key={skuFile?._id}
                  skuFile={skuFile}
                  isSelected={selectedImages?.some(
                    (selected) => selected?._id === skuFile?._id
                  )}
                  previewDimensions={previewDimensions}
                  onImageLoad={handleLoad}
                />
              ))}
          </div>
        </div>
        <div className="selected_image_section">
          <div className="batch_description_wrapper">
            {richTextHtml && (
              <>
                <div className="selected_image_header">Batch Description</div>
                <div className="selected_images_container">
                  <iframe
                    src={richTextHtml}
                    title="Rich Text Viewer"
                    style={{ border: "none", width: "100%", height: "100%" }}
                  />
                </div>
              </>
            )}
          </div>

          <div className="button_wrapper">
            <div className="left_wrapper">
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                // onChange={handleFileChange}
                onChange={(e) => handleFileChange(e.target.files)}
                multiple
                accept="image/png,image/jpg,image/jpeg, image/webp"
                webkitdirectory="true"
              />
              <Button
                variant="solid"
                onClick={() => setShowBatchSheetModal(true)}
                style={{ marginBottom: "10px", border: "2px solid #1890ff" }}
              >
                Auto Batch Sheet Rows ({skuDetails?.draftSku?.batchSheetRows?.length || 0})
              </Button>
              <Button
                variant="solid"
                onClick={handleUploadFileClick}
                style={{ marginBottom: "10px", border: "2px solid #1890ff" }}
                loading={isUploading}
                disabled={isUploading}
              >
                Upload File
              </Button>
              <Button
                variant="solid"
                onClick={handleDownloadCSV}
                style={{ marginBottom: "10px", border: "2px solid #1890ff" }}
                icon={<DownloadOutlined />}
              >
                Download CSV For Batch
              </Button>
              <div className="article_shade_selector">
                <span className="article_shade_label">Pack Size</span>
                <InputNumber
                  size="small"
                  min={2}
                  step={1}
                  precision={0}
                  value={packSize}
                  placeholder="null"
                  style={{ width: "100%" }}
                  onChange={(val) => setPackSize(val ?? null)}
                />
              </div>
              <div className="article_shade_selector">
                <span className="article_shade_label">Article Shade</span>
                <Radio.Group
                  size="small"
                  value={articleShade}
                  onChange={(e) => setArticleShade(e.target.value)}
                >
                  <Radio.Button value="light">Light</Radio.Button>
                  <Radio.Button value="dark">Dark</Radio.Button>
                </Radio.Group>
              </div>
              <Button
                color="default"
                variant="solid"
                onClick={() => handleResetVersion()}
              >
                Reset Version
              </Button>
              <Button
                variant="solid"
                onClick={() => handleSkipVersion()}
                style={{ backgroundColor: "#ff7a00", borderColor: "#ff7a00", color: "#fff" }}
              >
                Skip SKU
              </Button>
              {/* <Button
                variant="solid"
                onClick={() => handleCannotProcess()}
                style={{ backgroundColor: "#ff4d4f", borderColor: "#ff4d4f", color: "#fff" }}
              >
                Can't Process
              </Button> */}
            </div>
            <div className="submit_buttons_wrapper">
              <Button
                onClick={() => setShowSubmitContentModal(true)}
                className="submit_button submit_content_only"
              >
                Submit (Only Content)
              </Button>
              <Button
                onClick={() => handleSubmit(false, false)}
                className="submit_button submit_no_cc_no_color"
              >
                Submit (No CC, No Combo)
              </Button>
              <Button
                onClick={() => handleSubmit(false, true)}
                className="submit_button submit_no_cc_only_color"
              >
                Submit (Only Combo)
              </Button>
              <Button
                onClick={() => handleSubmit(true, false)}
                className="submit_button submit_only_cc"
              >
                Submit (Only CC)
              </Button>
              <Button
                onClick={() => handleSubmit(true, true)}
                className="submit_button submit_cc_and_color"
              >
                Submit (CC & Combo)
              </Button>
            </div>
          </div>
        </div>
      </div>
      {showRejectModal && getRejetSkuModal()}
      {showCannotProcessModal && getCannotProcessModal()}
      {showSubmitContentModal && getSubmitContentModal()}
      {showBatchSheetModal && getBatchSheetModal()}
      <div className="right_images_wrapper">
        <div className="grid_header">
          <div className="header_cell">Model</div>
          <div className="header_cell">Ghost</div>
          <div className="header_cell">Product</div>
          <div className="header_cell">Detail</div>
        </div>
        <div className="grid_container">
          {grid.map((row, rowIndex) => (
            <div key={rowIndex} className="grid_row">
              <div className="row_label">{rowIndex + 1}</div>
              {row.map((cell, colIndex) => (
                <GridDropCell
                  key={colIndex}
                  rowIndex={rowIndex}
                  colIndex={colIndex}
                  cell={cell}
                  isDisabled={disabledColumns[rowIndex][colIndex]}
                  onDelete={handleDeleteImage}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
    <DragOverlay dropAnimation={null}>
      {activeDraggedSku ? (
        <div className="bst2-drag-overlay">
          <img
            src={activeDraggedSku?.image?.thumbPath?.url}
            alt={activeDraggedSku?.image?.name}
          />
        </div>
      ) : null}
    </DragOverlay>
    </DndContext>
  );
};

export default BSTSeggregation;
